import { HttpRequest, HttpMethod } from "./http";

// Amaze (amaze-x) Seller Open API — Phase 0 surface only.
//
// Credentials are config-only (config.yml platform.amaze) exactly like Makro /
// Mirakl: there is no save form and no endpoint that returns a password, so the
// FE can only read status, test the connection and trigger a manual sync.
//
// Fulfilment methods (time-slot / ship / waybill / status / seller-note /
// order-request) and the WMS unmapped-orders screens belong to later phases and
// are intentionally NOT declared here yet.
class PlatformAmazeProvider extends HttpRequest {
    // GET connection status. Returns
    // { configured, env, username_masked, shop_id, amaze_id, shop_name }.
    getCredentials() {
        return this.request(HttpMethod.Get, "/api/platform/amaze/credentials");
    }

    // POST test connection (no body — credentials come from config.yml).
    // Returns { ok, shop_name, amaze_id, date_format_detected, rate_limit_rps }.
    testConnection() {
        return this.request(HttpMethod.Post, "/api/platform/amaze/test-connection", {});
    }

    // GET effective settings of THIS container (Backoffice). Returns
    // { enabled, sync_mode, ingest_statuses, stock_mode, page_size,
    //   rate_limit_rps, app_mode }.
    // NOTE: it cannot see the Webhook container's poller intervals — use
    // pollerStatus() for anything about the pollers.
    getSettings() {
        return this.request(HttpMethod.Get, "/api/platform/amaze/settings");
    }

    // POST manual order sync. payload: { time_from?, time_to? }.
    // 200 -> { job_id, status }; 422 -> { job_id, status, message } when a job
    // is already in flight (re-attach to that job_id, do not start a new one).
    ordersSync(payload) {
        return this.request(HttpMethod.Post, "/api/platform/amaze/orders/sync", payload || {});
    }

    // POST manual catalogue sync, no body. Same 200 / 422 contract as above.
    productsSync() {
        return this.request(HttpMethod.Post, "/api/platform/amaze/products/sync", {});
    }

    // GET poller health. Returns
    // { order_watermark_at, last_order_poll_at, last_product_poll_at,
    //   unmapped_orders, rate_limit_rps, app_mode }.
    // The pollers run in the Webhook container, so this is the only way the BO
    // screen can tell whether they are still alive.
    pollerStatus() {
        return this.request(HttpMethod.Get, "/api/platform/amaze/health");
    }

    // Shared sync-job progress poll (Issue #32) — same endpoint every platform
    // sync uses.
    syncJob(id) {
        return this.request(HttpMethod.Get, `/api/platform/v2/sync/jobs/${id}`);
    }

    // ---- Phase 3 catalogue mapping (#263 / bo-api#587, spec §6B.4) ----

    // GET the dropdown feed of the "จับคู่หมวด platform" page:
    // { categories, attributes, brands, shop_addresses, shipping_methods,
    //   auto_approve }.
    //
    // Passing category_group_id narrows `attributes` and `brands` to that
    // Amaze category — which is the only level Amaze attaches attributes to, so
    // the mandatory-attribute inputs cannot be built until a category is
    // chosen. The BE caches the answer for an hour, so calling it again on
    // every category change is cheap.
    //
    // 503 {code:"amaze_disabled"|"amaze_not_configured"|"amaze_env_unset"} —
    // three DIFFERENT codes on purpose; do not collapse them into one message.
    reference(categoryGroupId) {
        const params = {};
        if (categoryGroupId) params.category_group_id = categoryGroupId;
        return this.request(HttpMethod.Get, "/api/platform/v2/amaze/reference", params);
    }

    // GET the saved mappings for one platform (11 = Amaze, 10 = Mirakl/Makro).
    // Returns { maps: [{category_id, category_name, platform_category,
    //                   default_attributes, updated_at}] } — mapped categories
    // ONLY, so an unmapped seller category is one that is missing from here.
    categoryMapList(platformId, categoryId) {
        const params = { platform_id: Number(platformId) };
        if (categoryId) params.category_id = Number(categoryId);
        return this.request(HttpMethod.Get, "/api/platform/v2/category-map", params);
    }

    // PUT one mapping. Body: {platform_id, category_id, platform_category,
    // default_attributes} — platform_id and category_id are NUMBERS (the Go
    // handler binds int/int64 and answers 400 to a string).
    //
    // 400 {code:"category_path_mismatch"} when platform_category.category_path
    // does not END with category_group_id. The path INCLUDES the leaf; the
    // category object's own parent_path EXCLUDES it, and copying that one is
    // how the mismatch happens. utils/amazePush.js:categoryMapBody derives the
    // leaf from the path so this screen cannot produce that pair.
    categoryMapSave(payload) {
        return this.request(HttpMethod.Put, "/api/platform/v2/category-map", payload || {});
    }
}

export default PlatformAmazeProvider;
